import { useState, useEffect, useRef, useCallback } from "react";
import { FkOutline, Select as FkSelect } from "../product_config/formkit";
import {
  Search, Download, Upload, Plus, Trash2,
  ChevronRight, ChevronDown, Info, X, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { createUid, makeBlankDoc, getPreset, PRESET_ORDER } from "../../lib/coberturaPresets";

// ─── Brand tokens (matches yool-tokens.css) ───────────────────
const T = {
  black:   '#1A1A1A',
  cta:     '#007D6E',
  cyan:    '#06B6D4',
  navy:    '#0E2640',
  gray1:   '#54585A',
  muted:   '#8D8D8D',
  border:  '#E0E0E0',
  gray:    '#EBEBEB',
  page:    '#F7F7F7',
  canvas:  '#E2E2E5',
  rowHover:'#FAFAFA',
  superBg: '#000000',
  error:   '#ED4068',
};

// ─── Helpers ─────────────────────────────────────────────────

function cellAccent(text) {
  if (!text) return false;
  const t = String(text).trim();
  return /^\d+%$/.test(t) && parseInt(t, 10) >= 50;
}

function cellMuted(text) {
  if (!text) return true;
  const t = String(text).trim();
  return t === '—' || t === '-' || t.toLowerCase() === 'sin tope';
}

function rowMatches(row, q) {
  if (!q) return true;
  const hay = (row.name || '') + ' ' + Object.values(row.cells || {}).join(' ');
  return hay.toLowerCase().includes(q);
}

// ─── Inline editable input ────────────────────────────────────

function InlineInput({ value, onChange, placeholder = '', align = 'left', style = {}, className = '' }) {
  return (
    <input
      type="text"
      value={value || ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      style={{ textAlign: align, ...style }}
      className={`w-full bg-transparent border-0 outline-none rounded px-1 py-0.5 text-xs leading-4 ${className}`}
    />
  );
}

// ─── Coverage table ───────────────────────────────────────────

function CoverageTable({ doc, setDoc, search }) {
  const q = (search || '').trim().toLowerCase();
  const allCols = doc.columnGroups.flatMap((g) => g.columns);
  const totalCols = 1 + allCols.length;

  const updSuper      = (sid, patch) => setDoc((d) => ({ ...d, superGroups: d.superGroups.map((s) => s.id === sid ? { ...s, ...patch } : s) }));
  const removeSuper   = (sid)        => setDoc((d) => ({ ...d, superGroups: d.superGroups.filter((s) => s.id !== sid) }));
  const addRowGroupTo = (sid)        => setDoc((d) => ({
    ...d, superGroups: d.superGroups.map((s) => s.id !== sid ? s : ({
      ...s, rowGroups: [...s.rowGroups, { id: createUid('rg'), name: 'Nuevo grupo', rows: [{ id: createUid('r'), name: 'Nueva prestación', cells: {} }] }],
    })),
  }));
  const updGroup    = (gid, patch)        => setDoc((d) => ({ ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.map((g) => g.id === gid ? { ...g, ...patch } : g) })) }));
  const removeGroup = (gid)               => setDoc((d) => ({ ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.filter((g) => g.id !== gid) })) }));
  const addRow      = (gid)               => setDoc((d) => ({
    ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.map((g) => g.id !== gid ? g : ({ ...g, rows: [...g.rows, { id: createUid('r'), name: 'Nueva prestación', cells: {} }] })) })),
  }));
  const updRow      = (gid, rid, patch)   => setDoc((d) => ({
    ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.map((g) => g.id !== gid ? g : ({ ...g, rows: g.rows.map((r) => r.id === rid ? { ...r, ...patch } : r) })) })),
  }));
  const updCell     = (gid, rid, cid, v)  => setDoc((d) => ({
    ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.map((g) => g.id !== gid ? g : ({ ...g, rows: g.rows.map((r) => r.id !== rid ? r : ({ ...r, cells: { ...r.cells, [cid]: v } })) })) })),
  }));
  const removeRow   = (gid, rid)          => setDoc((d) => ({
    ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.map((g) => g.id !== gid ? g : ({ ...g, rows: g.rows.filter((r) => r.id !== rid) })) })),
  }));
  const updColGroup = (cgid, patch)        => setDoc((d) => ({ ...d, columnGroups: d.columnGroups.map((g) => g.id === cgid ? { ...g, ...patch } : g) }));
  const updCol      = (cgid, cid, patch)   => setDoc((d) => ({
    ...d, columnGroups: d.columnGroups.map((g) => g.id !== cgid ? g : ({ ...g, columns: g.columns.map((c) => c.id === cid ? { ...c, ...patch } : c) })),
  }));
  const addSuper    = ()                   => setDoc((d) => ({
    ...d, superGroups: [...d.superGroups, { id: createUid('sg'), name: 'Nuevo mega-grupo', rowGroups: [{ id: createUid('rg'), name: 'Nuevo grupo', rows: [{ id: createUid('r'), name: 'Primera prestación', cells: {} }] }] }],
  }));

  let anyVisible = false;
  doc.superGroups.forEach((s) => s.rowGroups.forEach((g) => { if (g.rows.some((r) => rowMatches(r, q))) anyVisible = true; }));

  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 8 }}>
      <table className="w-full border-collapse text-xs">
        <colgroup>
          <col style={{ minWidth: 240, width: '32%' }} />
          {allCols.map((c) => <col key={c.id} style={{ minWidth: 80 }} />)}
        </colgroup>
        <thead>
          <tr style={{ background: '#F1F1F1', borderBottom: `1px solid ${T.border}` }}>
            <th style={{ borderRight: `1px solid ${T.border}`, padding: '6px 8px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Coberturas
            </th>
            {doc.columnGroups.map((cg) => (
              <th key={cg.id} colSpan={cg.columns.length} style={{ borderRight: `1px solid ${T.border}`, padding: 4, textAlign: 'center' }}>
                <InlineInput
                  value={cg.name}
                  onChange={(v) => updColGroup(cg.id, { name: v })}
                  placeholder="Grupo de columnas"
                  align="center"
                  style={{ fontSize: 10, fontWeight: 600, color: T.gray1, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                />
              </th>
            ))}
          </tr>
          <tr style={{ background: '#fff', borderBottom: `2px solid ${T.border}` }}>
            <th style={{ borderRight: `1px solid ${T.border}`, padding: '6px 8px' }} />
            {doc.columnGroups.map((cg) =>
              cg.columns.map((c) => (
                <th key={c.id} style={{ borderRight: `1px solid ${T.border}`, padding: 4, textAlign: 'center', verticalAlign: 'bottom' }}>
                  <InlineInput
                    value={c.name}
                    onChange={(v) => updCol(cg.id, c.id, { name: v })}
                    placeholder="Columna"
                    align="center"
                    style={{ fontWeight: 500, color: T.black }}
                  />
                  {c.sub && <div style={{ fontSize: 9, color: T.muted, marginTop: 2 }}>{c.sub}</div>}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {doc.superGroups.map((s) => {
            const groups = s.rowGroups
              .map((g) => ({ ...g, rows: g.rows.filter((r) => rowMatches(r, q)) }))
              .filter((g) => (q ? g.rows.length > 0 : true));
            if (q && groups.length === 0) return null;

            return (
              <tr key={s.id + '-frag'} style={{ display: 'contents' }}>
                {s.name && (
                  <tr key={s.id + '-banner'} style={{ display: 'table-row', background: T.superBg, borderTop: `2px solid ${T.cyan}` }}>
                    <td colSpan={totalCols} style={{ padding: '6px 12px' }}>
                      <div className="flex items-center gap-2">
                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.5, background: 'rgba(255,255,255,.15)', padding: '2px 6px', borderRadius: 4, color: '#fff', whiteSpace: 'nowrap' }}>Mega-grupo</span>
                        <InlineInput
                          value={s.name}
                          onChange={(v) => updSuper(s.id, { name: v })}
                          placeholder="Nombre del mega-grupo"
                          style={{ color: '#fff', fontWeight: 600 }}
                          className="flex-1"
                        />
                        <button
                          onClick={() => addRowGroupTo(s.id)}
                          style={{ color: 'rgba(255,255,255,.7)', fontSize: 10, border: '1px solid rgba(255,255,255,.25)', borderRadius: 4, padding: '2px 8px', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                          onMouseOver={(e) => e.currentTarget.style.color = '#fff'}
                          onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,.7)'}
                        >
                          <Plus size={10} /> Grupo
                        </button>
                        <button
                          onClick={() => { if (window.confirm('¿Eliminar este mega-grupo y todo su contenido?')) removeSuper(s.id); }}
                          style={{ color: 'rgba(255,255,255,.5)', background: 'transparent', border: 0, cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex', flexShrink: 0 }}
                          onMouseOver={(e) => e.currentTarget.style.color = '#ff6b6b'}
                          onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,.5)'}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {groups.map((g) => (
                  <tr key={g.id + '-frag'} style={{ display: 'contents' }}>
                    <tr key={g.id + '-head'} style={{ display: 'table-row', background: T.navy }}>
                      <td colSpan={totalCols} style={{ padding: '5px 12px' }}>
                        <div className="flex items-center gap-2">
                          <InlineInput
                            value={g.name}
                            onChange={(v) => updGroup(g.id, { name: v })}
                            placeholder="Grupo de filas"
                            style={{ fontWeight: 600, color: '#fff', flex: 1 }}
                            className="flex-1"
                          />
                          {!q && (
                            <>
                              <button
                                onClick={() => addRow(g.id)}
                                style={{ color: 'rgba(255,255,255,.6)', fontSize: 10, border: '1px solid rgba(255,255,255,.2)', borderRadius: 4, padding: '2px 8px', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                                onMouseOver={(e) => e.currentTarget.style.color = '#fff'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,.6)'}
                              >
                                <Plus size={10} /> Fila
                              </button>
                              <button
                                onClick={() => removeGroup(g.id)}
                                style={{ color: 'rgba(255,255,255,.4)', background: 'transparent', border: 0, cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex', flexShrink: 0 }}
                                onMouseOver={(e) => e.currentTarget.style.color = '#ff6b6b'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255,255,255,.4)'}
                              >
                                <Trash2 size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {g.rows.map((row, ri) => (
                      <tr
                        key={row.id}
                        style={{ background: ri % 2 === 1 ? T.rowHover : '#fff', borderBottom: `1px solid ${T.border}` }}
                        onMouseOver={(e) => { e.currentTarget.style.background = '#F0FAF9'; }}
                        onMouseOut={(e) => { e.currentTarget.style.background = ri % 2 === 1 ? T.rowHover : '#fff'; }}
                        className="group/row"
                      >
                        <td style={{ borderRight: `1px solid ${T.border}`, padding: '3px 8px', verticalAlign: 'middle' }}>
                          <div className="flex items-center gap-1">
                            <span style={{ color: T.border, flexShrink: 0, width: 12 }}>·</span>
                            {q ? (
                              <span style={{ fontSize: 12, color: T.black }}>{row.name}</span>
                            ) : (
                              <InlineInput
                                value={row.name}
                                onChange={(v) => updRow(g.id, row.id, { name: v })}
                                placeholder="Nombre de prestación"
                                style={{ color: T.black, flex: 1 }}
                                className="flex-1"
                              />
                            )}
                            {!q && (
                              <button
                                onClick={() => removeRow(g.id, row.id)}
                                style={{ color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', flexShrink: 0, padding: 1, display: 'flex', opacity: 0 }}
                                className="group-hover/row:opacity-100 transition-opacity"
                                onMouseOver={(e) => e.currentTarget.style.color = T.error}
                                onMouseOut={(e) => e.currentTarget.style.color = T.muted}
                              >
                                <X size={11} />
                              </button>
                            )}
                          </div>
                        </td>
                        {allCols.map((c) => {
                          const v = row.cells[c.id] ?? '';
                          return (
                            <td
                              key={c.id}
                              style={{
                                borderRight: `1px solid ${T.border}`,
                                padding: '3px 4px',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                color: cellAccent(v) ? T.cta : cellMuted(v) ? T.muted : T.gray1,
                                fontWeight: cellAccent(v) ? 600 : 400,
                              }}
                            >
                              <InlineInput
                                value={v}
                                onChange={(val) => updCell(g.id, row.id, c.id, val)}
                                placeholder="—"
                                align={c.align || 'center'}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {!q && (
                      <tr key={g.id + '-add'} style={{ background: '#fff', borderBottom: `1px solid ${T.border}` }}>
                        <td colSpan={totalCols} style={{ padding: '5px 12px' }}>
                          <button
                            onClick={() => addRow(g.id)}
                            style={{ color: T.muted, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 0, cursor: 'pointer' }}
                            onMouseOver={(e) => e.currentTarget.style.color = T.cta}
                            onMouseOut={(e) => e.currentTarget.style.color = T.muted}
                          >
                            <Plus size={11} /> Agregar fila en {g.name.toLowerCase()}
                          </button>
                        </td>
                      </tr>
                    )}
                  </tr>
                ))}
              </tr>
            );
          })}
          {q && !anyVisible && (
            <tr>
              <td colSpan={totalCols} style={{ textAlign: 'center', padding: '40px 16px', color: T.muted }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: T.gray1, marginBottom: 4 }}>Sin resultados</div>
                <div style={{ fontSize: 12 }}>Ajusta la búsqueda para encontrar la prestación.</div>
              </td>
            </tr>
          )}
          {!q && (
            <tr style={{ background: T.page }}>
              <td colSpan={totalCols} style={{ padding: '8px 12px' }}>
                <button
                  onClick={addSuper}
                  style={{ color: T.muted, fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 0, cursor: 'pointer' }}
                  onMouseOver={(e) => e.currentTarget.style.color = T.cta}
                  onMouseOut={(e) => e.currentTarget.style.color = T.muted}
                >
                  <Plus size={11} /> Agregar mega-grupo
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Structure sidebar ────────────────────────────────────────

function StructureSidebar({ doc, setDoc }) {
  const [open, setOpen] = useState(() => {
    const o = {};
    doc.superGroups.forEach((s) => { o[s.id] = true; s.rowGroups.forEach((g) => { o[g.id] = false; }); });
    doc.columnGroups.forEach((g) => { o[g.id] = true; });
    return o;
  });
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const addColGroup    = () => setDoc((d) => ({ ...d, columnGroups: [...d.columnGroups, { id: createUid('cg'), name: 'Nuevo grupo', columns: [{ id: createUid('c'), name: 'Columna 1', align: 'center' }] }] }));
  const removeColGroup = (cgid) => { if (doc.columnGroups.length <= 1) { toast.error('Debe haber al menos un grupo de columnas'); return; } setDoc((d) => ({ ...d, columnGroups: d.columnGroups.filter((g) => g.id !== cgid) })); };
  const addCol         = (cgid) => setDoc((d) => ({ ...d, columnGroups: d.columnGroups.map((g) => g.id !== cgid ? g : ({ ...g, columns: [...g.columns, { id: createUid('c'), name: 'Nueva columna', align: 'center' }] })) }));
  const removeCol      = (cgid, cid) => setDoc((d) => ({ ...d, columnGroups: d.columnGroups.map((g) => g.id !== cgid ? g : ({ ...g, columns: g.columns.filter((c) => c.id !== cid) })) }));
  const updColName     = (cgid, cid, name) => setDoc((d) => ({ ...d, columnGroups: d.columnGroups.map((g) => g.id !== cgid ? g : ({ ...g, columns: g.columns.map((c) => c.id === cid ? { ...c, name } : c) })) }));
  const addSuper       = () => setDoc((d) => ({ ...d, superGroups: [...d.superGroups, { id: createUid('sg'), name: 'Nuevo mega-grupo', rowGroups: [{ id: createUid('rg'), name: 'Nuevo grupo', rows: [{ id: createUid('r'), name: 'Primera prestación', cells: {} }] }] }] }));
  const removeSuper    = (sid) => setDoc((d) => ({ ...d, superGroups: d.superGroups.filter((s) => s.id !== sid) }));
  const addRowGroup    = (sid) => setDoc((d) => ({ ...d, superGroups: d.superGroups.map((s) => s.id !== sid ? s : ({ ...s, rowGroups: [...s.rowGroups, { id: createUid('rg'), name: 'Nuevo grupo', rows: [{ id: createUid('r'), name: 'Primera prestación', cells: {} }] }] })) }));
  const removeRG       = (gid) => setDoc((d) => ({ ...d, superGroups: d.superGroups.map((s) => ({ ...s, rowGroups: s.rowGroups.filter((g) => g.id !== gid) })) }));

  const headStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 12px 6px', background: '#fff', position: 'sticky', top: 0, zIndex: 10,
    borderBottom: `1px solid ${T.border}`,
  };
  const labelStyle = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.muted };
  const addBtnStyle = { color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex' };

  return (
    <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${T.border}`, background: T.page, overflowY: 'auto' }}>

      {/* Columns */}
      <div style={{ borderBottom: `1px solid ${T.border}` }}>
        <div style={headStyle}>
          <span style={labelStyle}>Columnas</span>
          <button style={addBtnStyle} onClick={addColGroup} title="Agregar grupo"
            onMouseOver={(e) => e.currentTarget.style.color = T.cta}
            onMouseOut={(e) => e.currentTarget.style.color = T.muted}
          ><Plus size={13} /></button>
        </div>
        <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {doc.columnGroups.map((cg) => (
            <div key={cg.id} style={{ borderRadius: 6, border: `1px solid ${T.border}`, background: '#fff', overflow: 'hidden' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px', cursor: 'pointer' }}
                onClick={() => toggle(cg.id)}
                onMouseOver={(e) => e.currentTarget.style.background = T.rowHover}
                onMouseOut={(e) => e.currentTarget.style.background = '#fff'}
              >
                {open[cg.id] ? <ChevronDown size={11} style={{ color: T.muted, flexShrink: 0 }} /> : <ChevronRight size={11} style={{ color: T.muted, flexShrink: 0 }} />}
                <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: T.black, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cg.name || '—'}</span>
                <span style={{ fontSize: 10, color: T.muted }}>{cg.columns.length}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); removeColGroup(cg.id); }}
                  style={{ color: T.border, background: 'transparent', border: 0, cursor: 'pointer', marginLeft: 2, display: 'flex', padding: 1 }}
                  onMouseOver={(e) => { e.stopPropagation(); e.currentTarget.style.color = T.error; }}
                  onMouseOut={(e) => { e.currentTarget.style.color = T.border; }}
                ><Trash2 size={10} /></button>
              </div>
              {open[cg.id] && (
                <div style={{ borderTop: `1px solid ${T.gray}`, background: T.page, padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cg.columns.map((c) => (
                    <div key={c.id} className="group/col" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: T.border, fontSize: 10 }}>↳</span>
                      <input
                        type="text" value={c.name}
                        onChange={(e) => updColName(cg.id, c.id, e.target.value)}
                        style={{ flex: 1, fontSize: 11, background: 'transparent', border: 0, outline: 'none', color: T.gray1, minWidth: 0 }}
                        onFocus={(e) => e.target.style.color = T.black}
                        onBlur={(e) => e.target.style.color = T.gray1}
                      />
                      <button
                        onClick={() => removeCol(cg.id, c.id)}
                        style={{ color: T.border, background: 'transparent', border: 0, cursor: 'pointer', padding: 1, display: 'flex' }}
                        onMouseOver={(e) => e.currentTarget.style.color = T.error}
                        onMouseOut={(e) => e.currentTarget.style.color = T.border}
                        className="opacity-0 group-hover/col:opacity-100"
                      ><X size={10} /></button>
                    </div>
                  ))}
                  <button
                    onClick={() => addCol(cg.id)}
                    style={{ color: T.muted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2, background: 'transparent', border: 0, cursor: 'pointer' }}
                    onMouseOver={(e) => e.currentTarget.style.color = T.cta}
                    onMouseOut={(e) => e.currentTarget.style.color = T.muted}
                  ><Plus size={9} /> Columna</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Rows */}
      <div>
        <div style={headStyle}>
          <span style={labelStyle}>Filas</span>
          <button style={addBtnStyle} onClick={addSuper} title="Agregar mega-grupo"
            onMouseOver={(e) => e.currentTarget.style.color = T.cta}
            onMouseOut={(e) => e.currentTarget.style.color = T.muted}
          ><Plus size={13} /></button>
        </div>
        <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {doc.superGroups.map((s) => (
            <div key={s.id} style={{ borderRadius: 6, border: `1px solid ${T.border}`, background: '#fff', overflow: 'hidden' }}>
              {s.name && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px', cursor: 'pointer', background: T.gray }}
                  onClick={() => toggle(s.id)}
                  onMouseOver={(e) => e.currentTarget.style.background = T.border}
                  onMouseOut={(e) => e.currentTarget.style.background = T.gray}
                >
                  {open[s.id] ? <ChevronDown size={11} style={{ color: T.gray1, flexShrink: 0 }} /> : <ChevronRight size={11} style={{ color: T.gray1, flexShrink: 0 }} />}
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.black, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); if (window.confirm('¿Eliminar mega-grupo?')) removeSuper(s.id); }}
                    style={{ color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex', padding: 1 }}
                    onMouseOver={(e) => { e.stopPropagation(); e.currentTarget.style.color = T.error; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = T.muted; }}
                  ><Trash2 size={10} /></button>
                </div>
              )}
              {(!s.name || open[s.id]) && (
                <div style={{ padding: s.name ? '4px 8px' : '5px 8px', borderTop: s.name ? `1px solid ${T.gray}` : 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {s.rowGroups.map((g) => (
                    <div key={g.id} className="group/rg" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}>
                      <span style={{ color: T.border, fontSize: 10 }}>↳</span>
                      <span style={{ flex: 1, fontSize: 11, color: T.gray1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                      <span style={{ fontSize: 9, color: T.muted }}>{g.rows.length}</span>
                      <button
                        onClick={() => removeRG(g.id)}
                        style={{ color: T.border, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex', padding: 1 }}
                        onMouseOver={(e) => e.currentTarget.style.color = T.error}
                        onMouseOut={(e) => e.currentTarget.style.color = T.border}
                        className="opacity-0 group-hover/rg:opacity-100"
                      ><X size={10} /></button>
                    </div>
                  ))}
                  <button
                    onClick={() => addRowGroup(s.id)}
                    style={{ color: T.muted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2, background: 'transparent', border: 0, cursor: 'pointer' }}
                    onMouseOver={(e) => e.currentTarget.style.color = T.cta}
                    onMouseOut={(e) => e.currentTarget.style.color = T.muted}
                  ><Plus size={9} /> Grupo de filas</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Footnotes editor ─────────────────────────────────────────

function FootnotesEditor({ doc, setDoc }) {
  const upd = (i, val) => setDoc((d) => ({ ...d, footnotes: d.footnotes.map((f, idx) => (idx === i ? val : f)) }));
  const add = () => setDoc((d) => ({ ...d, footnotes: [...(d.footnotes || []), 'Nueva nota a pie de página.'] }));
  const del = (i) => setDoc((d) => ({ ...d, footnotes: d.footnotes.filter((_, idx) => idx !== i) }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Info size={13} style={{ color: T.muted }} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.muted }}>Notas y condiciones</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(doc.footnotes || []).map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: T.muted, fontSize: 12, marginTop: 4, flexShrink: 0 }}>{i + 1}.</span>
            <input
              type="text"
              value={f}
              onChange={(e) => upd(i, e.target.value)}
              style={{ flex: 1, fontSize: 12, color: T.gray1, background: T.page, border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 8px', outline: 'none' }}
              onFocus={(e) => { e.target.style.borderColor = T.cta; e.target.style.background = '#fff'; }}
              onBlur={(e) => { e.target.style.borderColor = T.border; e.target.style.background = T.page; }}
            />
            <button
              onClick={() => del(i)}
              style={{ color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', marginTop: 4, flexShrink: 0, display: 'flex' }}
              onMouseOver={(e) => e.currentTarget.style.color = T.error}
              onMouseOut={(e) => e.currentTarget.style.color = T.muted}
            ><X size={13} /></button>
          </div>
        ))}
      </div>
      <button
        onClick={add}
        style={{ color: T.muted, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 0, cursor: 'pointer' }}
        onMouseOver={(e) => e.currentTarget.style.color = T.cta}
        onMouseOut={(e) => e.currentTarget.style.color = T.muted}
      >
        <Plus size={12} /> Agregar nota
      </button>
    </div>
  );
}

// ─── Empty state: preset picker ──────────────────────────────

function PresetPicker({ onSelect, onImport }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 24, padding: 40 }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.muted, marginBottom: 6 }}>
          Sin configuración de coberturas
        </p>
        <p style={{ fontSize: 13, color: T.gray1 }}>
          Elige una plantilla para comenzar o importa un JSON existente
        </p>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
        {PRESET_ORDER.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            style={{
              width: 180, padding: '24px 16px', border: `1.5px solid ${T.border}`,
              borderRadius: 12, background: '#fff', cursor: 'pointer', textAlign: 'center',
              display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center',
            }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = T.cta; e.currentTarget.style.boxShadow = `0 0 0 3px ${T.cta}22`; }}
            onMouseOut={(e)  => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <FileText size={28} style={{ color: T.cta }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.black }}>{p.label}</span>
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onImport}
        style={{ fontSize: 12, color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        onMouseOver={(e) => e.currentTarget.style.color = T.black}
        onMouseOut={(e)  => e.currentTarget.style.color = T.muted}
      >
        <Upload size={13} /> Importar JSON existente
      </button>
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────

export default function CoberturasTab({ formData, handleChange }) {
  const [doc, setDoc] = useState(null);
  const [search, setSearch] = useState('');
  const hasInitialized = useRef(false);

  // Parse json_coberturas from formData — waits for async plan load
  useEffect(() => {
    if (hasInitialized.current) return;
    if (formData.json_coberturas) {
      hasInitialized.current = true;
      try {
        const parsed = JSON.parse(formData.json_coberturas);
        if (parsed?.columnGroups && parsed?.superGroups) setDoc(parsed);
      } catch {}
    }
  }, [formData.json_coberturas]);

  // Propagate doc changes to parent formData
  const updateDoc = useCallback((updater) => {
    setDoc((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      handleChange({ target: { name: 'json_coberturas', value: JSON.stringify(next) } });
      return next;
    });
  }, [handleChange]);

  const loadPreset = (presetId) => {
    if (!presetId) return;
    const hasContent = doc.superGroups?.some((s) => s.rowGroups?.some((g) => g.rows?.length > 1));
    if (hasContent && !window.confirm('Se reemplazará el contenido actual con la plantilla. ¿Continuar?')) return;
    updateDoc(getPreset(presetId));
    toast.success('Plantilla cargada');
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (doc.title || 'coberturas').toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('JSON exportado');
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          if (data.columnGroups && (data.rowGroups || data.superGroups)) {
            const migrated = !data.superGroups && data.rowGroups
              ? { ...data, superGroups: [{ id: createUid('sg'), name: '', rowGroups: data.rowGroups }] }
              : data;
            updateDoc(migrated);
            toast.success('Configuración importada');
          } else {
            toast.error('Ese archivo no es una configuración válida de coberturas');
          }
        } catch {
          toast.error('No se pudo leer el archivo JSON');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const rowCount = doc?.superGroups?.reduce((n, s) => n + s.rowGroups.reduce((m, g) => m + g.rows.length, 0), 0) ?? 0;

  // Show preset picker when no doc loaded yet
  if (!doc) {
    return (
      <div style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif" }}>
        <PresetPicker
          onSelect={(id) => { updateDoc(getPreset(id)); toast.success('Plantilla cargada'); }}
          onImport={handleImport}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif" }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.muted, pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar prestación o cobertura…"
            style={{ width: '100%', boxSizing: 'border-box', height: 40, padding: '0 34px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: '#FBFBFB', color: T.black, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: T.muted, background: 'transparent', border: 0, cursor: 'pointer', display: 'flex' }}
              onMouseOver={(e) => e.currentTarget.style.color = T.black}
              onMouseOut={(e) => e.currentTarget.style.color = T.muted}
            ><X size={13} /></button>
          )}
        </div>

        <div style={{ width: 200 }}>
          <FkSelect
            value=""
            placeholder="Cargar plantilla…"
            options={PRESET_ORDER.map((p) => ({ value: p.id, label: p.label }))}
            onChange={(e) => loadPreset(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <FkOutline onClick={handleImport}><Upload size={14} /> Importar JSON</FkOutline>
          <FkOutline onClick={handleExport}><Download size={14} /> Exportar JSON</FkOutline>
        </div>
      </div>

      {/* Canvas — gray background, floating doc card */}
      <div style={{ background: T.canvas, borderRadius: 12, padding: '20px 16px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 12px 32px rgba(14,38,64,.10)', overflow: 'hidden' }}>

          {/* Doc header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <FileText size={16} style={{ color: T.cta, marginTop: 4, flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="text"
                    value={doc.eyebrow || ''}
                    onChange={(e) => updateDoc((d) => ({ ...d, eyebrow: e.target.value }))}
                    placeholder="EYEBROW · PLAN COMPLEMENTARIO DE SALUD"
                    style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.muted, background: 'transparent', border: 0, outline: 'none', flex: 1 }}
                    onFocus={(e) => e.target.style.background = T.page}
                    onBlur={(e) => e.target.style.background = 'transparent'}
                  />
                  <span style={{ fontSize: 10, color: T.muted, flexShrink: 0, whiteSpace: 'nowrap' }}>{rowCount} prestaciones</span>
                </div>
                <input
                  type="text"
                  value={doc.title || ''}
                  onChange={(e) => updateDoc((d) => ({ ...d, title: e.target.value }))}
                  placeholder="Título del cuadro de coberturas"
                  style={{ fontSize: 20, fontWeight: 700, color: T.black, background: 'transparent', border: 0, outline: 'none', width: '100%', letterSpacing: '-0.03em', lineHeight: 1.2 }}
                  onFocus={(e) => e.target.style.background = T.page}
                  onBlur={(e) => e.target.style.background = 'transparent'}
                />
                <input
                  type="text"
                  value={doc.subtitle || ''}
                  onChange={(e) => updateDoc((d) => ({ ...d, subtitle: e.target.value }))}
                  placeholder="Descripción breve (póliza, vigencia…)"
                  style={{ fontSize: 13, color: T.gray1, background: 'transparent', border: 0, outline: 'none', width: '100%' }}
                  onFocus={(e) => e.target.style.background = T.page}
                  onBlur={(e) => e.target.style.background = 'transparent'}
                />
              </div>
            </div>
          </div>

          {/* Split layout: sidebar + table */}
          <div style={{ display: 'flex', minHeight: 400 }}>
            <StructureSidebar doc={doc} setDoc={updateDoc} />
            <div style={{ flex: 1, overflow: 'auto' }}>
              <CoverageTable doc={doc} setDoc={updateDoc} search={search} />
            </div>
          </div>

          {/* Footnotes */}
          <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, background: T.page }}>
            <FootnotesEditor doc={doc} setDoc={updateDoc} />
          </div>

        </div>
      </div>

    </div>
  );
}
